/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button } from 'components/Button';
import { LoadingScreen } from 'components/LoadingScreen';
import { Modal } from 'components/Modal';
import { apiErrorHandler, SizeEnum, NotificationTypes } from 'utils';
import { columns } from './data/claim-automation-data';
import { useStore } from 'context/store-context';
import { withNotification } from 'components/Notification';
import { ClaimAutomationRuleModal } from 'components/ClaimAutomationRuleModal';
import {
  getStatementInputConfig,
  transformRuleStatementsToSave,
  getNewRuleTemplate,
  validateAndUpdateRule,
  getRuleNaturalLanguageSummary,
} from './ClaimAutomation/utils';
import { GET_CLAIM_AUTOMATION_RULES } from 'gql/queries';
import {
  UPDATE_CLAIM_AUTOMATION_RULE,
  REMOVE_CLAIM_AUTOMATION_RULE,
  UPDATE_CLAIM_AUTOMATION_RULE_PRIORITY,
} from 'gql/mutations';
import { DndDataTable } from 'components/DNTable/DndDataTable';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import {
  PlusIcon,
  CheckCircleIcon,
  LightningBoltIcon,
  ExclamationIcon,
} from '@heroicons/react/outline';

export const AutomationRules = withNotification(({ showNotification }: any) => {
  const store = useStore();
  const { storeId } = store;

  const [rules, setRules] = useState<any[]>([]);
  const [initialRules, setInitialRules] = useState<any[]>([]);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<string | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<any>(null);
  const mutationPending = React.useRef(false);
  const closeEditor = (value: any) => {
    if (mutationPending.current) return;
    setValidationErrors(null);
    setEditingRule(value);
  };

  const [getClaimAutomationRules, { loading, error: loadError }] = useLazyQuery(
    GET_CLAIM_AUTOMATION_RULES,
    {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        const fetchedRules = data?.claimRules || [];
        setRules(fetchedRules);
        setInitialRules(fetchedRules);
      },
    },
  );

  const [saveClaimAutomationRule, { loading: savingRule }] = useMutation(
    UPDATE_CLAIM_AUTOMATION_RULE,
    {
      onError(error) {
        mutationPending.current = false;
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        mutationPending.current = false;
        showNotification(
          NotificationTypes.success,
          'Your claim automation rule is saved successfully!',
        );
        const saved = data?.saveClaimRules || [];
        setRules(saved);
        setInitialRules(saved);
        setEditingRule(null);
        setValidationErrors(null);
      },
    },
  );

  const [updateClaimRulePriority, { loading: updatingRulePriority }] =
    useMutation(UPDATE_CLAIM_AUTOMATION_RULE_PRIORITY, {
      onError(error) {
        mutationPending.current = false;
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
        setRules(initialRules);
      },
      onCompleted(data) {
        mutationPending.current = false;
        showNotification(
          NotificationTypes.success,
          'Your claim automation rule priority was changed successfully!',
        );
        const updated = data?.changeClaimRulePriority || [];
        setRules(updated);
        setInitialRules(updated);
        setValidationErrors(null);
      },
    });

  const [deleteClaimAutomationRule, { loading: deletingRule }] = useMutation(
    REMOVE_CLAIM_AUTOMATION_RULE,
    {
      onError(error) {
        mutationPending.current = false;
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        mutationPending.current = false;
        showNotification(
          NotificationTypes.success,
          'Your claim automation rule is removed successfully!',
        );
        const remaining = data?.deleteClaimAutomationRules || [];
        setRules(remaining);
        setInitialRules(remaining);
        setRuleToDelete(null);
      },
    },
  );

  const busy = savingRule || updatingRulePriority || deletingRule;
  const runMutation = (mutation: any, variables: any) => {
    if (mutationPending.current || busy || !storeId) return;
    mutationPending.current = true;
    Promise.resolve(mutation({ variables })).catch(() => {
      mutationPending.current = false;
    });
  };

  useEffect(() => {
    if (storeId) {
      setRules([]);
      setInitialRules([]);
      closeEditor(null);
      setRuleToDelete(null);
      getClaimAutomationRules({
        variables: {
          storeId,
        },
      });
    }
  }, [storeId]);

  const parseOrReconstructRule = (rule: any) => {
    let parsed: any = null;
    if (rule.ruleBeforeTransform) {
      try {
        parsed = JSON.parse(rule.ruleBeforeTransform);
      } catch (e) {
        console.warn('Failed to parse ruleBeforeTransform', e);
      }
    }

    if (
      parsed &&
      typeof parsed === 'object' &&
      Array.isArray(parsed.statements) &&
      parsed.statements.every(
        (statement: any) =>
          statement &&
          typeof statement.expressionField === 'string' &&
          typeof statement.expression === 'string',
      )
    ) {
      return {
        ...parsed,
        id: rule.id,
        title: rule.title,
        active: rule.active,
        action: rule.action,
        statements: parsed.statements.map((s: any) => ({
          ...s,
          config: getStatementInputConfig(s.expressionField, s.expression),
        })),
        actionNotes: rule.actionNotes ?? parsed.actionNotes ?? '',
        refundShipping: rule.refundShipping ?? parsed.refundShipping ?? false,
        notify: rule.notify ?? parsed.notify ?? true,
      };
    }

    // Fallback: reconstruct from backend fields
    const rawStatements = Array.isArray(rule?.statements)
      ? rule.statements
      : [];

    return {
      id: rule?.id,
      title: rule?.title || 'Untitled Rule',
      active: rule?.active !== false,
      action: rule?.action || '',
      actionNotes: rule?.actionNotes || '',
      refundShipping: Boolean(rule?.refundShipping),
      notify: rule?.notify !== false,
      statements: rawStatements.map((s: any, idx: number) => {
        const stmtObj = s && typeof s === 'object' ? s : {};
        const key = Object.keys(stmtObj)[0] || '';
        const fieldData =
          stmtObj[key] && typeof stmtObj[key] === 'object' ? stmtObj[key] : {};
        const exprObj =
          fieldData.expression && typeof fieldData.expression === 'object'
            ? fieldData.expression
            : {};
        const opKey = Object.keys(exprObj)[0] || (key ? '_eq' : '');

        let prevConnector = 'AND';
        if (idx > 0) {
          const prevStmt = rawStatements[idx - 1];
          const prevObj =
            prevStmt && typeof prevStmt === 'object' ? prevStmt : {};
          const prevValues = Object.values(prevObj);
          const prevFieldData: any =
            prevValues[0] && typeof prevValues[0] === 'object'
              ? prevValues[0]
              : null;
          if (
            prevFieldData &&
            typeof prevFieldData.connector === 'string' &&
            prevFieldData.connector.trim() !== ''
          ) {
            prevConnector = prevFieldData.connector;
          }
        }

        return {
          expressionField: key,
          expression: opKey,
          expressionValue: exprObj[opKey] ?? '',
          connector: idx === 0 ? '' : prevConnector,
          config: getStatementInputConfig(key, opKey),
        };
      }),
    };
  };

  const handleEditRule = (rule: any) => {
    const editableRule = parseOrReconstructRule(rule);
    closeEditor(editableRule);
  };

  const handleCloneRule = (rule: any) => {
    const editableRule = parseOrReconstructRule(rule);
    closeEditor({
      ...editableRule,
      id: null,
      title: `${editableRule.title} (Copy)`,
    });
  };

  const handleConfirmDelete = () => {
    if (!ruleToDelete) return;
    runMutation(deleteClaimAutomationRule, {
      input: {
        id: ruleToDelete.id,
        storeId,
      },
    });
  };

  const handleUpdateRuleStatus = (rule: any) => {
    const editable = parseOrReconstructRule(rule);
    runMutation(saveClaimAutomationRule, {
      input: {
        id: rule.id,
        storeId,
        title: rule.title,
        active: !rule.active,
        statements: rule.statements,
        action: rule.action,
        priority: rule.priority,
        actionNotes: editable.actionNotes || '',
        refundShipping: Boolean(editable.refundShipping),
        ruleBeforeTransform: JSON.stringify({
          ...editable,
          active: !rule.active,
        }),
      },
    });
  };

  const handleUpdateRulePriority = (
    newRulesOrder: any,
    rule: any,
    ruleIndex: number,
  ) => {
    if (busy || mutationPending.current) return;
    setRules(newRulesOrder);
    runMutation(updateClaimRulePriority, {
      input: {
        id: rule.id,
        storeId,
        priority: ruleIndex + 1,
      },
    });
  };

  const handleSaveRule = (rule: any) => {
    const { hasError, messages, statements } = validateAndUpdateRule(rule);
    if (hasError) {
      setValidationErrors(messages.join(', '));
      return;
    }
    const transformedStatements = transformRuleStatementsToSave(statements);
    const ruleIndex = rules.findIndex((item: any) => item.id === rule.id);

    runMutation(saveClaimAutomationRule, {
      input: {
        id: rule.id,
        storeId,
        title: rule.title,
        active: rule.active,
        statements: transformedStatements,
        action: rule.action,
        priority: ruleIndex >= 0 ? ruleIndex + 1 : rules.length + 1,
        actionNotes: rule.actionNotes || '',
        refundShipping: Boolean(rule.refundShipping),
        ruleBeforeTransform: JSON.stringify(rule),
      },
    });
  };

  // Metrics computation
  const activeRulesCount = rules.filter((r) => r.active).length;

  if (store?.storeLoading && !store?.storeProperties) return <LoadingScreen />;

  return (
    <SettingsViewWrapper activeNav="claim-automations">
      <div>
        {/* Rule Editor Modal */}
        <ClaimAutomationRuleModal
          show={!!editingRule}
          setEditingRule={closeEditor}
          editingRule={editingRule}
          handleSaveRule={handleSaveRule}
          validationErrors={validationErrors}
          isLoading={savingRule}
        />

        {/* Delete Confirmation Modal */}
        {ruleToDelete && (
          <Modal
            open={Boolean(ruleToDelete)}
            closeDisabled={busy}
            openModal={() => !busy && setRuleToDelete(null)}
            containerClass="sm:max-w-md sm:w-full"
          >
            <div className="p-2 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                  <ExclamationIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Delete Automation Rule
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <p className="text-sm text-slate-600 leading-relaxed">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-slate-900">
                  {`"${ruleToDelete.title}"`}
                </span>
                ? New incoming claims will no longer be automated by this rule.
              </p>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setRuleToDelete(null)}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <Button
                  onClick={handleConfirmDelete}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-4 py-2 rounded-lg"
                  isLoading={deletingRule}
                >
                  Confirm Delete
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                Resolutions & Policies
              </span>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight mt-0.5">
                Claim Automations
              </h1>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                Configure visual logic rules to instantly approve refunds,
                trigger replacements, or deny invalid claims without manual
                agent review.
              </p>
            </div>

            <Button
              onClick={() => closeEditor(getNewRuleTemplate())}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm px-4 py-2.5 rounded-lg whitespace-nowrap"
              size={SizeEnum.lg}
              disabled={busy || loading || !storeId}
            >
              <PlusIcon className="w-4 h-4" />
              New Automation Rule
            </Button>
          </div>

          {/* Metrics / KPI Banner */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <LightningBoltIcon className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-medium text-slate-500">
                  Active Rules
                </span>
                <h4 className="text-lg font-bold text-slate-900">
                  {activeRulesCount}{' '}
                  <span className="text-xs font-normal text-slate-400">
                    of {rules.length} total
                  </span>
                </h4>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-medium text-slate-500">
                  Paused Rules
                </span>
                <h4 className="text-lg font-bold text-slate-900">
                  {rules.length - activeRulesCount}{' '}
                  <span className="text-xs font-normal text-slate-400">
                    not evaluating claims
                  </span>
                </h4>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold">#1</span>
              </div>
              <div className="min-w-0">
                <span className="text-xs font-medium text-slate-500">
                  Highest Priority Rule
                </span>
                <h4 className="text-sm font-bold text-slate-900 break-words">
                  {rules.find((rule) => rule.active)?.title ||
                    'No active rules'}
                </h4>
              </div>
            </div>
          </div>
        </div>

        {/* Rules Table */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-6 pb-12">
          <div
            className={`table-container bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${
              updatingRulePriority ? 'cursor-wait opacity-80' : ''
            }`}
          >
            <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap gap-2 items-center justify-between bg-slate-50/50">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Evaluation Order
                </span>
                <span className="text-xs text-slate-400">
                  (Drag rows to change execution priority)
                </span>
              </div>
              <span className="text-xs font-medium text-slate-500">
                {rules.length} rule{rules.length === 1 ? '' : 's'} defined
              </span>
            </div>

            {loadError ? (
              <div role="alert" className="p-6 text-sm text-slate-700">
                <p>We couldn&apos;t load your automation rules. Try again.</p>
                <Button
                  onClick={() =>
                    getClaimAutomationRules({ variables: { storeId } })
                  }
                >
                  Retry
                </Button>
              </div>
            ) : loading ? (
              <div role="status" className="p-6 text-sm text-slate-500">
                Loading automation rules…
              </div>
            ) : rules.length === 0 ? (
              <div className="p-8 text-center">
                <h2 className="font-semibold text-slate-900">
                  No automation rules yet
                </h2>
                <p className="text-sm text-slate-500 mt-2">
                  Create a rule to resolve matching claims automatically. Review
                  each condition before activating it.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto claim-automation-table">
                <DndDataTable
                  data={rules.map((rule: any, index: number) => {
                    const ruleObj = parseOrReconstructRule(rule);

                    return {
                      ...rule,
                      refundShipping: ruleObj.refundShipping,
                      busy,
                      index,
                      priority: rule.priority ?? index + 1,
                      ruleSummary: getRuleNaturalLanguageSummary(ruleObj),
                      updateRuleStatus: () => handleUpdateRuleStatus(rule),
                      editRule: () => handleEditRule(rule),
                      cloneRule: () => handleCloneRule(rule),
                      deleteRule: () => setRuleToDelete(rule),
                    };
                  })}
                  columns={columns}
                  setData={handleUpdateRulePriority}
                  loading={busy || loading}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </SettingsViewWrapper>
  );
});
